import { Set } from 'immutable';
import type { ID } from '../types';
import BaseObject from './BaseObject';
import Occasion, { timelineDateDisplay } from '../helpers/Occasion';
import { NoteReminder } from './Note';
import Collection from './Collection';

type TimelineItemType = 'note' | 'interaction';

export interface TimelineItemUser {
  name: string;
  email: string;
  avatar_url: string;
  is_self: boolean;
}

interface ClientURLs {
  superhuman_url?: string;
}

export const noteOrReminderAction = (reminder: NoteReminder) =>
  !!reminder?.date ? 'set a reminder for' : 'added a note';

// The last two don't conform to the API spec, but are used in the UI for convenience of display and filtering
export type InteractionSource =
  | 'event'
  | 'email'
  | 'apple-message'
  | 'note'
  | 'reminder'
  | 'call'
  | 'whatsapp-message'
  // these codes are used on the items that show where the contact was imported from
  | 'CL'
  | 'TW'
  | 'IG'
  | 'FB'
  | 'LI'
  | 'EM'
  | 'ME'
  | 'MA'
  | 'BI'
  | 'SS'
  | 'phone'
  | 'messages'
  | 'AC'
  | 'GC'
  | 'WA'
  | 'AB'
  | 'BX'; // Browser extension

export class Interaction {
  source: InteractionSource;
  date: Occasion;
  _link: string;
  user: TimelineItemUser;
  title?: string;
  actionTaken?: string;
  client_urls: ClientURLs = {};

  constructor(props: any = {}) {
    this.source = props.source;
    this.date = new Occasion(props.date);
    this._link = props.metadata.link || props.metadata.links?.url;
    this.user = props.user;
    this.title = props.metadata.title;
    this.actionTaken = props.actionTaken;

    if (props.metadata.links?.superhuman_url || props.client_urls?.superhuman_url) {
      this.client_urls.superhuman_url =
        props.metadata?.links?.superhuman_url || props.client_urls?.superhuman_url;
    }
  }

  get iconKey() {
    switch (this.source) {
      case 'event':
      case 'CL':
        return 'calendar';
      case 'email':
      case 'EM':
      case 'ME':
        return 'email';
      case 'apple-message':
      case 'AB':
        return 'apple-messages';
      case 'WA':
      case 'whatsapp-message':
        return 'whatsapp';
      case 'MA':
      case 'BI':
      case 'SS':
      case 'BX':
        return 'manual';
      case 'TW':
        return 'twitter';
      case 'FB':
        return 'facebook';
      case 'LI':
        return 'linkedin';
      case 'phone':
      case 'call':
        return 'phone';
      case 'messages':
        return 'messages';
      case 'AC':
      case 'GC':
        return 'contacts';
      case 'IG':
        return 'instagram';
      default:
        return null;
    }
  }

  get dateDisplay() {
    return timelineDateDisplay(this.date);
  }

  get sidebarDateDisplay() {
    if (this.date.isToday) {
      return this.dateDisplay;
    }

    if (this.date.isThisYear) {
      return this.date.format('MMMM d');
    }

    return this.date.format('MMMM d, yyyy');
  }

  private get clientLink(): string | null {
    let client = null;

    if (typeof window !== 'undefined') {
      client = window.localStorage.getItem(`${this.source}_client`);
    }

    if (client) {
      return this.client_urls[`${client}_url`] || null;
    }

    return null;
  }

  get link() {
    return this.clientLink || this._link;
  }
}

export class TimelineItem extends BaseObject {
  stableID: ID;
  type: TimelineItemType;
  user: TimelineItemUser;
  noteID: ID;
  reminder?: NoteReminder;
  interaction: Interaction;
  date: string;

  constructor(props: any = {}) {
    super(props);

    this.stableID = props.stableID || props.id;
    this.type = props.item_type || props.type;
    this.user = props.users[0] || props.user;
    this.date = props.date;

    if (this.type === 'note') {
      this.noteID = props.id.split(':')[1] || props.noteID;
      this.reminder = props.metadata?.reminder;

      this.interaction = new Interaction({
        id: props.id.split(':')[1],
        source: this.reminder ? 'reminder' : 'note',
        date: props.date,
        user: props.users[0],
        actionTaken: noteOrReminderAction(this.reminder),
        metadata: {},
      });
    } else if (this.type === 'interaction') {
      this.interaction = new Interaction({
        id: props.id.split(':')[1],
        source: props.source,
        date: props.date,
        user: props.users[0],
        actionTaken: props.content,
        metadata: props.metadata,
      });
    }
  }
}

export class ContactTimeline extends Collection<TimelineItem> {
  condensedViewIDs: Set<ID>;
  noteIDs: Set<ID>;
  showBtnAfterID?: string;
  windowLoaded?: boolean;

  constructor(props: any = {}) {
    super(props);

    this.showBtnAfterID = props.showBtnAfterID;
    this.condensedViewIDs = props.condensedView ? Set(this.items.keySeq()) : props.condensedViewIDs;
    this.windowLoaded = !!props.windowLoaded;

    if (props.items) {
      this.noteIDs = this.items
        .filter((item) => item.type === 'note')
        .map((item) => item.noteID)
        .toSet();
    } else {
      this.noteIDs = Set();
    }
  }

  get upcomingItems() {
    return this.items.filter((item) => {
      if (!item.interaction?.date) {
        return false;
      } else {
        return !item.interaction?.date.isPast;
      }
    });
  }

  get pastItems() {
    return this.items.filter((item) => !item.interaction?.date || item.interaction?.date.isPast);
  }

  get condensedPastItems() {
    return this.pastItems.filter((item) => this.condensedViewIDs.has(item.id));
  }

  // for a passed in start and end date, return the counts of items that fall within that window in the timeline
  itemsInWindow(start: Occasion, end: Occasion) {
    const loadedItemsInWindow = this.items.filter((item) => {
      return new Occasion(item?.date).isInRange(end.date, start.date);
    });

    // TODO: not sure this is properly accounting for things like messages and reminders
    return loadedItemsInWindow.reduce(
      (itemCountByType, item) => {
        if (item.type === 'note') {
          return {
            ...itemCountByType,
            notes: itemCountByType.notes + 1,
            total: itemCountByType.total + 1,
          };
        } else {
          const type = item.id.split(':')[0];

          if (type === 'email') {
            return {
              ...itemCountByType,
              emails: itemCountByType.emails + 1,
              total: itemCountByType.total + 1,
            };
          } else if (type === 'event') {
            return {
              ...itemCountByType,
              events: itemCountByType.events + 1,
              total: itemCountByType.total + 1,
            };
          } else if (type === 'call') {
            return {
              ...itemCountByType,
              calls: itemCountByType.calls + 1,
              total: itemCountByType.total + 1,
            };
          }
        }

        return itemCountByType;
      },
      { emails: 0, events: 0, notes: 0, calls: 0, total: 0 },
    );
  }
}

export class TimelineWindowSummary extends BaseObject {
  start: string;
  emails: number;
  events: number;
  notes: number;
  calls: number;
  total: number;

  constructor(props: any = {}) {
    super(props);

    // these are counts for items that are in the specified window and already are loaded in the app's timeline state
    const { calls, emails, events, notes, total } = props.adjustments || {
      calls: 0,
      emails: 0,
      events: 0,
      notes: 0,
      total: 0,
    };

    this.start = props.next_date;
    this.calls = (props.calls || 0) - calls;
    this.emails = (props.emails || 0) - emails;
    this.events = (props.events || 0) - events;
    this.notes = (props.notes || 0) - notes;
    this.total = (props.total || 0) - total;
  }
}

// for notes/reminders, dump them into main cache and only store IDs here? ugh fuck
export class FilteredTimelineCollection extends Collection<TimelineItem> {
  has_next: boolean;
  lastDate?: string;

  constructor(props: any = {}) {
    super(props);

    this.has_next = props.has_next;
    this.lastDate = props.last_date || props.lastDate;
  }

  get canLoadMore(): boolean {
    return this.has_next;
  }
}

export type TimelineFilter = 'emails' | 'events' | 'notes' | 'reminders' | 'calls';
// we just want pagination on these collections, shouldn't need anything fancy vis a vis the ContactTimeline class
export type FilteredTimelineCollectionsMap = Record<TimelineFilter, FilteredTimelineCollection>;
